import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { updateProgrammeObligation, renameObligation, deleteProgrammeObligation } from 'services/complianceService'
import FormTemplateField from 'features/taskManagement/components/FormTemplateField'
import GenerateFormFromDocument, { FormGenLink } from 'features/taskManagement/components/GenerateFormFromDocument'
import UserPicker from 'features/taskManagement/components/UserPicker'
import SearchableSelect from 'components/SearchableSelect'
import { listContractors } from 'services/contractorService'
import { getTeams, getProjects } from 'services/taskService'
import { CADENCE_PRESETS, CRITICALITY_LEVELS, closestCadencePreset } from 'features/compliance/obligationConstants'

const closestPreset = days => {
  if (days === null || days === undefined) {
    return CADENCE_PRESETS.find(p => p.days === null)
  }
  let best = null; let diff = Infinity
  for (const p of CADENCE_PRESETS) {
    if (p.days === null) continue
    const d = Math.abs(p.days - days)
    if (d < diff) { diff = d; best = p }
  }
  return best
}

const closestCriticality = w => {
  let best = CRITICALITY_LEVELS[1]; let diff = Infinity
  for (const c of CRITICALITY_LEVELS) {
    const d = Math.abs(c.value - (w || 3))
    if (d < diff) { diff = d; best = c }
  }
  return best
}

const computeNextDueFromCadence = (days) => {
  if (!days) return null
  const d = new Date()
  d.setDate(d.getDate() + days)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const CustomiseObligationModal = ({ bpId, bpName, obligation, availableTags = [], availableDomains = [], onClose, onSaved }) => {
  const [cadenceDays, setCadenceDays] = useState(() => {
    const init = obligation?.cadenceDays ?? obligation?.cadence_days ?? null
    const max = obligation?.statutoryMaxCadenceDays ?? obligation?.statutory_max_cadence_days ?? null
    return max && init && init > max ? max : init
  })
  const [cadenceWeekday, setCadenceWeekday] = useState(obligation?.cadenceWeekday ?? obligation?.cadence_weekday ?? null)
  const storedNextDue = obligation?.nextDueAt ?? obligation?.next_due_at ?? null
  const [nextDueAt, setNextDueAt] = useState(() => {
    if (storedNextDue) return String(storedNextDue).slice(0, 10)
    return computeNextDueFromCadence(obligation?.cadenceDays ?? obligation?.cadence_days ?? null)
  })
  const [nextDueIsEstimate, setNextDueIsEstimate] = useState(!storedNextDue)

  const applyCadence = (days) => {
    setCadenceDays(days)
    if (nextDueIsEstimate) {
      const computed = computeNextDueFromCadence(days)
      if (computed) setNextDueAt(computed)
    }
  }
  const [taskDomainId, setTaskDomainId] = useState(obligation?.taskDomainId ?? obligation?.task_domain_id ?? null)
  const [toleranceDays, setToleranceDays] = useState(obligation?.toleranceDays ?? obligation?.tolerance_days ?? 0)
  const [criticality, setCriticality] = useState(obligation?.criticalityWeight ?? obligation?.criticality_weight ?? 3)
  const [skipped, setSkipped] = useState(obligation?._intent === 'skip' || !!(obligation?.isSkipped ?? obligation?.is_skipped))
  const [skipReason, setSkipReason] = useState(obligation?.skipReason ?? obligation?.skip_reason ?? '')
  const [tagIds, setTagIds] = useState(() => {
    const initial = obligation?.acceptedTagIds ?? obligation?.accepted_tag_ids ?? []
    return new Set(initial)
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isCustomObligation = (obligation?.isSystem ?? obligation?.is_system) === false
  const [name, setName] = useState(obligation?.name ?? '')
  const defaultFormId = obligation?.defaultFormTemplateId ?? obligation?.default_form_template_id ?? ''
  const explicitFormId = obligation?.formTemplateId ?? obligation?.form_template_id ?? ''
  const effectiveFormId = explicitFormId || defaultFormId
  const [formTemplateId, setFormTemplateId] = useState(effectiveFormId)
  const [applyFormToOpenTasks, setApplyFormToOpenTasks] = useState(false)
  const initialAssigneeIds = obligation?.defaultAssigneeIds ?? obligation?.default_assignee_ids ?? []
  const initialApproverIds = obligation?.defaultApproverIds ?? obligation?.default_approver_ids ?? []
  const initialContractorId = obligation?.defaultContractorId ?? obligation?.default_contractor_id ?? null
  const initialContractor = obligation?.defaultContractor ?? obligation?.default_contractor ?? null
  const initialAssignees = obligation?.defaultAssignees ?? obligation?.default_assignees ?? []
  const initialApprovers = obligation?.defaultApprovers ?? obligation?.default_approvers ?? []
  const [defaultAssigneeIds, setDefaultAssigneeIds] = useState(initialAssigneeIds)
  const [defaultApproverIds, setDefaultApproverIds] = useState(initialApproverIds)
  const [defaultContractorId, setDefaultContractorId] = useState(initialContractorId)
  const [applyPeopleToOpenTasks, setApplyPeopleToOpenTasks] = useState(true)
  const [contractors, setContractors] = useState([])
  const initialTaskTeamId = obligation?.defaultTaskTeamId ?? obligation?.default_task_team_id ?? null
  const initialTaskProjectId = obligation?.defaultTaskProjectId ?? obligation?.default_task_project_id ?? null
  const initialTaskTeam = obligation?.defaultTaskTeam ?? obligation?.default_task_team ?? null
  const initialTaskProject = obligation?.defaultTaskProject ?? obligation?.default_task_project ?? null
  const [defaultTaskTeamId, setDefaultTaskTeamId] = useState(initialTaskTeamId)
  const [defaultTaskProjectId, setDefaultTaskProjectId] = useState(initialTaskProjectId)
  const [teams, setTeams] = useState([])
  const [projects, setProjects] = useState([])
  const displayBpName = bpName || null

  const isFileObligation = (obligation?.kind === 'file_type' || obligation?.obligationKind === 'file_type')
  const initialFormTemplateId = effectiveFormId
  const formChanged = (formTemplateId || '') !== (initialFormTemplateId || '')

  const toggleTag = id => setTagIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const tagsByLayer = React.useMemo(() => {
    const groups = {}
    availableTags.forEach(t => {
      const k = t.layer || 'other'
      if (!groups[k]) groups[k] = []
      groups[k].push(t)
    })
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [availableTags])

  useEffect(() => {
    if (isFileObligation) return
    let live = true
    listContractors().then(res => { if (live) setContractors(res?.data?.data?.contractors || []) }).catch(() => {})
    getTeams().then(res => { if (live) setTeams(res?.data?.data || []) }).catch(() => {})
    getProjects().then(res => { if (live) setProjects((res?.data?.data || []).filter(p => (p.buildingPassportId ?? p.building_passport_id) === bpId)) }).catch(() => {})
    return () => { live = false }
  }, [isFileObligation, bpId])

  const sameIdSet = (a, b) => (a || []).length === (b || []).length && [...(a || [])].sort().join(',') === [...(b || [])].sort().join(',')
  const peopleChanged = !sameIdSet(defaultAssigneeIds, initialAssigneeIds) || !sameIdSet(defaultApproverIds, initialApproverIds) || (defaultContractorId || null) !== (initialContractorId || null) || (defaultTaskTeamId || null) !== (initialTaskTeamId || null) || (defaultTaskProjectId || null) !== (initialTaskProjectId || null)
  const cadencePreset = closestPreset(cadenceDays)
  const critPreset = closestCriticality(criticality)
  const statutoryMax = obligation?.statutoryMaxCadenceDays ?? obligation?.statutory_max_cadence_days ?? null
  const formatInterval = (days) => {
    if (!days) return ''
    if (days % 365 === 0) { const y = days / 365; return `${y} year${y > 1 ? 's' : ''}` }
    if (days % 30 === 0 && days < 365) { const m = days / 30; return `${m} month${m > 1 ? 's' : ''}` }
    if (days % 7 === 0 && days < 365) { const w = days / 7; return `${w} week${w > 1 ? 's' : ''}` }
    return `${days} days`
  }

  const save = async () => {
    setSaving(true)
    try {
      const trimmedName = name.trim()
      const nameChanged = bpId && trimmedName && trimmedName !== (obligation?.name ?? '')
      let renamedTasks = 0
      if (nameChanged) {
        const res = await renameObligation(bpId, obligation.id, trimmedName)
        renamedTasks = res?.data?.data?.renamedTasks ?? 0
      }

      const payload = {
        cadence_days: cadenceDays,
        cadence_weekday: cadenceDays === 7 ? cadenceWeekday : null,
        next_due_at: nextDueAt || null,
        tolerance_days: toleranceDays,
        criticality: critPreset.key,
        is_skipped: skipped,
        skip_reason: skipped ? (skipReason || null) : null,
      }
      if (taskDomainId) {
        payload.task_domain_id = taskDomainId
      }
      if (isFileObligation) {
        payload.accepted_tag_ids = Array.from(tagIds)
      } else {
        payload.form_template_id = (formTemplateId && formTemplateId !== defaultFormId) ? formTemplateId : null
        if (formChanged && applyFormToOpenTasks) {
          payload.apply_form_to_open_tasks = true
        }
        if (peopleChanged) {
          if (!sameIdSet(defaultAssigneeIds, initialAssigneeIds)) payload.default_assignee_ids = defaultAssigneeIds
          if (!sameIdSet(defaultApproverIds, initialApproverIds)) payload.default_approver_ids = defaultApproverIds
          if ((defaultContractorId || null) !== (initialContractorId || null)) payload.default_contractor_id = defaultContractorId || null
          if ((defaultTaskTeamId || null) !== (initialTaskTeamId || null)) payload.default_task_team_id = defaultTaskTeamId || null
          if ((defaultTaskProjectId || null) !== (initialTaskProjectId || null)) payload.default_task_project_id = defaultTaskProjectId || null
          if (applyPeopleToOpenTasks) {
            payload.apply_people_to_open_tasks = true
          }
        }
      }
      await updateProgrammeObligation(bpId, obligation.id, payload)

      if (nameChanged) {
        toast.success(
          renamedTasks > 0
            ? `Obligation renamed for your organisation: ${renamedTasks} open task${renamedTasks === 1 ? '' : 's'} updated too.`
            : 'Obligation renamed for your organisation.'
        )
      } else {
        toast.success('Obligation updated.')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Could not update obligation.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!bpId || deleting) return
    setDeleting(true)
    try {
      await deleteProgrammeObligation(bpId, obligation.id)
      toast.success('Custom obligation deleted.')
      onSaved?.()
      onClose()
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Could not delete this obligation.')
      setDeleting(false)
    }
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Eyebrow>Customise obligation</Eyebrow>
            <Title>{obligation?.name}</Title>
          </HeaderText>
          <CloseBtn onClick={onClose}><CloseRoundedIcon /></CloseBtn>
        </Header>

        <Body>
          {bpId && (
            <Section>
              <SectionHead>
                <SectionTitle>Obligation name</SectionTitle>
                <ScopeTag $org>Organisation-wide</ScopeTag>
              </SectionHead>
              <SectionNote>
                Shared across <strong>every building in your organisation</strong>. Not just this one. Renaming also updates any not-yet-completed tasks; completed tasks keep their original name.
              </SectionNote>
              <NameInput
                type='text'
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='Obligation name'
                maxLength={200}
              />
            </Section>
          )}

          <SectionHead>
            <SectionTitle>Settings for this building</SectionTitle>
            <ScopeTag title={displayBpName || undefined}>{displayBpName || 'This building only'}</ScopeTag>
          </SectionHead>
          <SectionNote>
            These apply only to {displayBpName ? <strong>{displayBpName}</strong> : 'this Building Passport'}. Other buildings keep their own settings.
          </SectionNote>

          <Field>
            <FieldLabel>Cadence. How often this is required</FieldLabel>
            <Pills>
              {CADENCE_PRESETS.map(p => {
                const blocked = statutoryMax ? p.days > statutoryMax : false
                return (
                  <Pill
                    key={p.label}
                    $active={p === cadencePreset}
                    style={blocked ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    title={blocked ? `Not allowed: this statutory obligation must happen at least every ${formatInterval(statutoryMax)}` : undefined}
                    onClick={() => { if (!blocked) applyCadence(p.days) }}
                  >{p.label}</Pill>
                )
              })}
            </Pills>
            <SmallNote>
              Or set a custom interval:
              <CustomInput
                type='number'
                min={1}
                max={statutoryMax || 7300}
                value={cadenceDays ?? ''}
                placeholder='days'
                onChange={e => {
                  const v = e.target.value ? Number(e.target.value) : null
                  applyCadence(statutoryMax && v && v > statutoryMax ? statutoryMax : v)
                }}
              />
              <SmallNoteText>days</SmallNoteText>
            </SmallNote>
            {cadenceDays === 7 && (
              <WeekdayRow>
                <WeekdayLabel>On which day of the week?</WeekdayLabel>
                <SearchableSelect
                  value={cadenceWeekday}
                  onChange={v => setCadenceWeekday(v)}
                  options={[
                    { value: 1, label: 'Monday' },
                    { value: 2, label: 'Tuesday' },
                    { value: 3, label: 'Wednesday' },
                    { value: 4, label: 'Thursday' },
                    { value: 5, label: 'Friday' },
                    { value: 6, label: 'Saturday' },
                    { value: 7, label: 'Sunday' },
                  ]}
                  clearable
                  clearLabel='Any day'
                  placeholder='Any day'
                  ariaLabel='Day of the week'
                />
              </WeekdayRow>
            )}
            {statutoryMax ? (
              <div style={{ marginTop: 8, fontSize: 13, color: '#4b5563', background: '#f3f4f6', borderRadius: 6, padding: '8px 10px', lineHeight: 1.4 }}>
                This is a statutory obligation. The law sets a maximum interval of <strong>{formatInterval(statutoryMax)}</strong>, so you can make it happen more often, but not less often.
              </div>
            ) : null}
          </Field>

          <Field>
            <FieldLabel>Next due date</FieldLabel>
            <WeekdayRow>
              <DateInput
                type='date'
                value={nextDueAt || ''}
                onChange={e => { setNextDueAt(e.target.value || null); setNextDueIsEstimate(false) }}
              />
              {nextDueAt && (
                <ClearDateLink type='button' onClick={() => { setNextDueAt(null); setNextDueIsEstimate(true) }}>Clear</ClearDateLink>
              )}
            </WeekdayRow>
            <DateHint>
              The next date this obligation is due. The recurring schedule and the calendar forecast both count forward from this date, so setting a real date here stops obligations bunching on the same day. If nothing has been recorded yet we show an estimate you can adjust. Use Clear to let the system work it out automatically.
            </DateHint>
          </Field>

          {availableDomains.length > 0 && (
            <Field>
              <FieldLabel>Domain. Which area this belongs to</FieldLabel>
              <SearchableSelect
                value={taskDomainId}
                onChange={v => setTaskDomainId(v)}
                options={availableDomains.map(d => ({ value: d.id, label: d.name }))}
                clearable
                clearLabel='No specific area'
                placeholder='No specific area'
                ariaLabel='Domain'
              />
            </Field>
          )}

          {!isFileObligation && (
            <Field>
              <FieldLabel>Form. What every task for this obligation asks people to complete</FieldLabel>
              <FormTemplateField
                value={formTemplateId || ''}
                onChange={template => setFormTemplateId(template?.id || defaultFormId)}
                buildingPassportId={bpId}
                programmeObligationId={obligation?.id}
                allowNone
                noneLabel='Use the standard completion record (a simple sign-off)'
                emptyHint='Tasks for this obligation are signed off on the standard completion record.'
              />
              {bpId && obligation?.id && (
                <GenerateFormFromDocument
                  buildingPassportId={bpId}
                  targetType='programme_obligation'
                  targetId={obligation.id}
                >
                  {open => (
                    <FormGenLink type='button' onClick={open} data-tour-id='formGen.open.customise'>
                      Or create one from a document
                    </FormGenLink>
                  )}
                </GenerateFormFromDocument>
              )}
              <FormHint>
                New tasks for this obligation use this form. By default that is the standard completion record, a simple sign-off. Choose a more detailed form if this obligation needs specific checks recorded.
              </FormHint>
              {formChanged && formTemplateId && formTemplateId !== defaultFormId && (
                <CheckRow>
                  <input
                    type='checkbox'
                    id='applyFormOpen'
                    checked={applyFormToOpenTasks}
                    onChange={e => setApplyFormToOpenTasks(e.target.checked)}
                  />
                  <label htmlFor='applyFormOpen'>
                    <strong>Also update tasks that are already open.</strong> This switches this obligation’s unfinished tasks to the new form now and clears any answers already filled in on them. This cannot be undone. Leave unticked to change future tasks only.
                  </label>
                </CheckRow>
              )}
            </Field>
          )}

          {!isFileObligation && (
            <Field>
              <FieldLabel>Defaults applied to every task for this obligation</FieldLabel>
              <FormHint>
                These are set on every new task created for this obligation, so you choose the usual people, team and project once here instead of setting each task by hand.
              </FormHint>
              <UserPicker
                label='Default assignees'
                value={defaultAssigneeIds}
                onChange={setDefaultAssigneeIds}
                buildingPassportId={bpId}
                placeholder='Search people to assign…'
                initialUsers={initialAssignees}
              />
              <UserPicker
                label='Default approvers'
                value={defaultApproverIds}
                onChange={setDefaultApproverIds}
                buildingPassportId={bpId}
                placeholder='Search people who sign off…'
                initialUsers={initialApprovers}
              />
              <div>
                <FieldLabel as='span'>Default contractor</FieldLabel>
                <SearchableSelect
                  value={defaultContractorId}
                  onChange={v => setDefaultContractorId(v)}
                  options={[
                    ...contractors.map(c => ({ value: c.id, label: c.name, sublabel: c.company || undefined })),
                    ...(initialContractor && !contractors.some(c => c.id === initialContractorId)
                      ? [{ value: initialContractorId, label: initialContractor.name }]
                      : []),
                  ]}
                  clearable
                  clearLabel='No contractor'
                  placeholder='No contractor'
                  searchPlaceholder='Search contractors…'
                  ariaLabel='Default contractor'
                />
              </div>
              <div>
                <FieldLabel as='span'>Default team</FieldLabel>
                <SearchableSelect
                  value={defaultTaskTeamId}
                  onChange={v => setDefaultTaskTeamId(v)}
                  options={[
                    ...teams.map(t => ({ value: t.id, label: t.name })),
                    ...(initialTaskTeam && !teams.some(t => t.id === initialTaskTeamId)
                      ? [{ value: initialTaskTeamId, label: initialTaskTeam.name }]
                      : []),
                  ]}
                  clearable
                  clearLabel='No team'
                  placeholder='No team'
                  searchPlaceholder='Search teams…'
                  ariaLabel='Default team'
                />
              </div>
              <div>
                <FieldLabel as='span'>Default project</FieldLabel>
                <SearchableSelect
                  value={defaultTaskProjectId}
                  onChange={v => setDefaultTaskProjectId(v)}
                  options={[
                    ...projects.map(p => ({ value: p.id, label: p.name })),
                    ...(initialTaskProject && !projects.some(p => p.id === initialTaskProjectId)
                      ? [{ value: initialTaskProjectId, label: initialTaskProject.name }]
                      : []),
                  ]}
                  clearable
                  clearLabel='No project'
                  placeholder='No project'
                  searchPlaceholder='Search projects…'
                  ariaLabel='Default project'
                />
              </div>
              {peopleChanged && (
                <CheckRow>
                  <input
                    type='checkbox'
                    id='applyPeopleOpen'
                    checked={applyPeopleToOpenTasks}
                    onChange={e => setApplyPeopleToOpenTasks(e.target.checked)}
                  />
                  <label htmlFor='applyPeopleOpen'>
                    <strong>Also update tasks that are already open.</strong> This puts these people on this obligation’s unfinished tasks now. Leave unticked to change future tasks only.
                  </label>
                </CheckRow>
              )}
            </Field>
          )}

          <Field>
            <FieldLabel>Grace period (tolerance)</FieldLabel>
            <SmallNote>
              Allow up to
              <CustomInput
                type='number'
                min={0}
                max={365}
                value={toleranceDays}
                onChange={e => setToleranceDays(Number(e.target.value) || 0)}
              />
              <SmallNoteText>days late before the obligation is flagged expired</SmallNoteText>
            </SmallNote>
          </Field>

          <Field>
            <FieldLabel>Criticality. How heavily this counts in the score</FieldLabel>
            <Pills>
              {CRITICALITY_LEVELS.map(c => (
                <Pill
                  key={c.value}
                  $active={c === critPreset}
                  $tint={c.colour}
                  onClick={() => setCriticality(c.value)}
                >{c.label}</Pill>
              ))}
            </Pills>
          </Field>

          {isFileObligation && availableTags.length > 0 && (
            <>
              <Divider />
              <Field>
                <FieldLabel>Required file tags (optional)</FieldLabel>
                <SmallNoteText>
                  Only files carrying <strong>all</strong> of the selected tags will count. Leave empty to accept any file of the matching classification.
                </SmallNoteText>
                {tagsByLayer.map(([layer, tags]) => (
                  <TagLayer key={layer}>
                    <TagLayerName>{layer}</TagLayerName>
                    <Pills>
                      {tags.map(t => (
                        <TagPill
                          key={t.id}
                          $active={tagIds.has(t.id)}
                          onClick={() => toggleTag(t.id)}
                        >{t.name}</TagPill>
                      ))}
                    </Pills>
                  </TagLayer>
                ))}
              </Field>
            </>
          )}

          <Divider />

          <Field>
            <FieldLabel>Not applicable to this Building Passport?</FieldLabel>
            <Toggle>
              <ToggleBtn $active={!skipped} onClick={() => setSkipped(false)}>Applies</ToggleBtn>
              <ToggleBtn $active={skipped} onClick={() => setSkipped(true)}>Not applicable</ToggleBtn>
            </Toggle>
            {skipped && (
              <TextArea
                placeholder='Why is this not applicable? (e.g. no gas supply, no lift, no sprinkler system)'
                value={skipReason}
                onChange={e => setSkipReason(e.target.value)}
                rows={3}
              />
            )}
          </Field>
        </Body>

        <Footer>
          {isCustomObligation && bpId && (
            confirmDelete ? (
              <DeleteConfirmWrap>
                <DeleteConfirmText>Delete this custom obligation for good?</DeleteConfirmText>
                <DeleteConfirmBtn onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </DeleteConfirmBtn>
                <Cancel onClick={() => setConfirmDelete(false)} disabled={deleting}>Keep</Cancel>
              </DeleteConfirmWrap>
            ) : (
              <DeleteBtn onClick={() => setConfirmDelete(true)} disabled={saving}>Delete obligation</DeleteBtn>
            )
          )}
          <FooterSpacer />
          <Cancel onClick={onClose}>Cancel</Cancel>
          <Save onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Save>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default CustomiseObligationModal

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 19, 43, 0.45);
  z-index: 1200;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%; max-width: 580px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 48px rgba(0,0,0,0.18);
`

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid #f4f4f6;
`

const HeaderText = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`

const Eyebrow = styled.span`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Title = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;
  &:hover { background: #f4f4f6; }
`

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 22px;
`

const Field = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Section = styled.div`
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fbfbfe;
  display: flex; flex-direction: column; gap: 10px;
`

const SectionHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
`

const SectionTitle = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const ScopeTag = styled.span`
  flex: 0 0 auto;
  max-width: 55%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $org }) => ($org ? '#eef2ff' : '#f1f3f5')};
  color: ${({ $org }) => ($org ? '#4338ca' : '#475569')};
`

const SectionNote = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};

  strong { color: ${({ theme }) => theme.colors.haiti}; font-weight: 700; }
`

const Pills = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
`

const Pill = styled.button`
  font: inherit;
  background: ${({ $active, $tint, theme }) => $active ? ($tint ? `${$tint}1A` : '#fff') : theme.colors.lighterGrey};
  border: 1px solid ${({ $active, $tint }) => $active ? ($tint ? `${$tint}66` : '#0c1229') : '#ececec'};
  color: ${({ $active, $tint, theme }) => $active && $tint ? $tint : theme.colors.haiti};
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  &:hover { background: ${({ $tint, theme }) => $tint ? `${$tint}1A` : theme.colors.lightGrey}; }
`

const SmallNote = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SmallNoteText = styled.span` font-size: 12px; `

const CustomInput = styled.input`
  width: 70px;
  padding: 4px 8px;
  font: inherit;
  font-size: 12.5px;
  border: 1px solid #ececec;
  border-radius: 6px;
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const WeekdayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`

const WeekdayLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const DateInput = styled.input`
  padding: 6px 10px;
  font: inherit;
  font-size: 13px;
  border: 1px solid #ececec;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const ClearDateLink = styled.button`
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  &:hover { color: #b00020; }
`

const DateHint = styled.p`
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const NameInput = styled.input`
  font: inherit;
  font-size: 14px;
  padding: 10px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const FormHint = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #7c2d12;

  label { cursor: pointer; }
  input { margin-top: 2px; flex-shrink: 0; cursor: pointer; }
`

const TagLayer = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin-top: 6px;
`

const TagLayerName = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const TagPill = styled.button`
  font: inherit;
  background: ${({ $active }) => $active ? '#33becc1A' : '#fff'};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : '#ececec'};
  color: ${({ $active }) => $active ? '#1f8c97' : '#0c1229'};
  font-size: 11.5px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  &:hover { background: #33becc15; }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f4f4f6;
  margin: 0;
`

const Toggle = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 999px;
  padding: 3px;
`

const ToggleBtn = styled.button`
  font: inherit;
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ theme }) => theme.colors.haiti};
  border: none;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};
`

const TextArea = styled.textarea`
  font: inherit;
  font-size: 13px;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 10px 12px;
  resize: vertical;
  min-height: 60px;
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f4f4f6;
`

const Cancel = styled.button`
  font: inherit;
  background: none;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Save = styled.button`
  font: inherit;
  background: linear-gradient(135deg, #33becc 0%, #1f8c97 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(51, 190, 204, 0.32);
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`

const FooterSpacer = styled.div` flex: 1; `

const DeleteBtn = styled.button`
  font: inherit;
  background: none;
  border: 1px solid #f0c5bd;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #b00020;
  cursor: pointer;
  &:hover:not(:disabled) { background: #fdecea; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const DeleteConfirmWrap = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`

const DeleteConfirmText = styled.span`
  font-size: 12.5px; font-weight: 700; color: #b00020;
`

const DeleteConfirmBtn = styled.button`
  font: inherit;
  background: #b00020;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`
